import styled from "styled-components";
import { Link } from "react-router-dom";
import { Section, theme, glassCardStyles } from "../styles";

export const PricingSection = styled(Section)`
  position: relative;
  padding: 64px 16px;
  scroll-margin-top: 72px;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 96px 24px;
    scroll-margin-top: 88px;
  }
`;

export const PricingGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    hsl(32 95% 55% / 0.05) 50%,
    transparent 100%
  );
`;

export const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  max-width: 1152px;
  margin: 0 auto;
  align-items: stretch;

  @media (min-width: 768px) {
    gap: 32px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const PlanCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 24px;
  border-radius: 16px;
  ${glassCardStyles}
  border: 1px solid ${theme.borderSoft};
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;

  ${({ $popular }) =>
    $popular &&
    `
    border-color: hsl(32 95% 55% / 0.5);
    box-shadow: 0 0 40px hsl(32 95% 55% / 0.15), ${theme.shadowCard};
  `}

  &:hover {
    border-color: hsl(32 95% 55% / 0.5);
  }

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

export const PopularBadge = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 16px;
  border-radius: 999px;
  background-image: ${theme.gradientGold};
  color: ${theme.primaryForeground};
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
`;

export const SavingsBadge = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  max-width: 45%;
  padding: 4px 12px;
  border-radius: 999px;
  background: hsl(32 95% 55% / 0.2);
  color: ${theme.primary};
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.3;
  text-align: right;
`;

export const PlanHead = styled.div`
  margin-bottom: 24px;
  padding-right: ${({ $hasSavings }) => ($hasSavings ? "96px" : "0")};
`;

export const PlanName = styled.h3`
  margin: 0 0 8px;
  color: ${theme.foreground};
  font-size: 1.5rem;
  font-weight: 700;
`;

export const PlanDescription = styled.p`
  margin: 0;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  line-height: 1.45;
`;

export const PriceBlock = styled.div`
  margin-bottom: 32px;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 2px;
`;

export const PriceAmount = styled.span`
  color: ${theme.foreground};
  font-size: 3rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
`;

export const PricePeriod = styled.span`
  color: ${theme.textMuted};
  font-size: 1rem;
`;

export const PriceMeta = styled.p`
  margin: 8px 0 0;
  color: ${theme.textSoft};
  font-size: 0.8125rem;
`;

export const FeatureList = styled.ul`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  margin: 0 0 32px;
  padding: 0;
  list-style: none;
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: hsl(var(--hp-foreground) / 0.9);
  font-size: 0.9375rem;
  line-height: 1.45;
`;

export const FeatureCheck = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: ${theme.primary};

  .material-icons {
    font-size: 20px;
  }
`;

export const PlanCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  margin-top: auto;
  border-radius: ${theme.radiusLg};
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  ${({ $popular }) =>
    $popular
      ? `
    border: none;
    background-image: ${theme.gradientGold};
    color: ${theme.primaryForeground};
    box-shadow: ${theme.shadowButton};

    &:hover,
    &&:hover {
      transform: scale(1.02);
      box-shadow: 0 6px 30px hsl(32 95% 55% / 0.5);
      color: ${theme.primaryForeground};
      text-decoration: none;
    }
  `
      : `
    border: 1px solid hsl(32 95% 55% / 0.5);
    background: transparent;
    background-image: none;
    color: ${theme.foreground};

    &:hover,
    &&:hover {
      background: hsl(32 95% 55% / 0.1);
      border-color: ${theme.primary};
      color: ${theme.foreground};
      text-decoration: none;
    }
  `}
`;

export const Guarantee = styled.p`
  margin: 48px 0 0;
  color: ${theme.textMuted};
  font-size: 1rem;
  text-align: center;
`;

export const PricingStatus = styled.p`
  margin: 0;
  color: ${theme.textMuted};
  font-size: 1rem;
  text-align: center;
`;
